import InfoIcon from '@mui/icons-material/Info';
import { Banner, Flex, Text } from '@components';
import ChoiceInput from '@components/ChoiceInput/ChoiceInput';
import { useUserQuery } from '@features/api/userSlice';
import { Box, useTheme } from '@mui/material';
import InvestmentFooter from '@pages/Investment/InvestmentFooter/InvestmentFooter';
import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  individualSteps,
  legalEntitySteps,
  legalEntityStepsData,
  stepsData,
} from './Progress.consts';
import { ContentContainer } from './Progress.styles';
import { Steps } from './Progress.types';
import { noop } from 'lodash';
import { InvestmentContainer } from '../Investment.styles';
import { boldenText } from '@utils/intl/intlUtils';

const companyTypes = ['legal_entity', 'nominee'];

const Progress: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const intl = useIntl();
  const { sm: isMobile } = useMediaQueries();
  const { state } = useLocation();
  const { productId } = useParams();

  const { data: user } = useUserQuery();

  const [completedSteps] = useState<Steps[]>(
    state?.completedSteps ?? [Steps.None]
  );

  const progressType = state?.type ?? 'individual';

  const steps = useMemo(() => {
    return companyTypes.includes(progressType)
      ? legalEntitySteps
      : individualSteps;
  }, [progressType]);

  const stepData = useMemo(() => {
    const typedStepsData = companyTypes.includes(progressType)
      ? legalEntityStepsData
      : stepsData;

    if (completedSteps.includes(Steps.ContractSignature)) {
      return typedStepsData[Steps.ContractSignature];
    }

    if (completedSteps.includes(Steps.IdentityVerification)) {
      return typedStepsData[Steps.IdentityVerification];
    }

    if (completedSteps.includes(Steps.Details)) {
      return typedStepsData[Steps.Details];
    }

    return typedStepsData[Steps.None];
  }, [completedSteps, progressType]);

  const onSubmit = async () => {
    if (stepData.button.getNavigatePath) {
      navigate(stepData.button.getNavigatePath(productId), {
        state: {
          progress: stepData?.progress ?? state?.progress,
          type: progressType,
        },
        replace: true,
      });
    } else {
      navigate(`/investment-subscription/${productId}/contract-signature`, {
        state: {
          progress: 9,
          productId: productId,
        },
        replace: true,
      });
    }
  };

  const onPreviousPress = () => {
    if (stepData.onPreviousPress) {
      navigate(
        stepData.onPreviousPress.to(productId),
        stepData.onPreviousPress.options
      );
    } else {
      noop();
    }
  };
  if (!user) return <></>;
  return (
    <InvestmentContainer data-testid='progress-page-container'>
      <ContentContainer>
        <Text
          variant='h3'
          fontWeight='fontWeightBold'
          mt={5}
          color={theme.palette.black.A100}
        >
          <FormattedMessage
            id={stepData.title}
            values={{
              firstName: user?.basicDetails.firstName,
            }}
          />
        </Text>
        <Text
          variant='h6'
          mt={5}
          fontWeight='fontWeightMedium'
          color={theme.palette.black[500]}
        >
          {stepData.message ? <FormattedMessage id={stepData.message} /> : null}
        </Text>
        <Flex
          sx={{
            marginTop: theme.spacing(12),
            flexDirection: 'column',
            maxWidth: {
              xs: '100%',
              md: '75%',
            },
          }}
        >
          <ChoiceInput
            direction='column'
            choices={steps}
            readonly
            disableRipple
            showIconCompletedOnSelected
            defaultValues={completedSteps ?? []}
          />
          {completedSteps.includes(Steps.ContractSignature) ? (
            <Flex mt={12}>
              <Banner color='warning' icon={InfoIcon}>
                <Text
                  variant='bodyBig'
                  fontWeight='fontWeightMedium'
                  color={theme.palette.text.primary}
                  whiteSpace='pre-line'
                >
                  <FormattedMessage id='investment.progress.contractSignatureBannerContent' />
                </Text>
              </Banner>
            </Flex>
          ) : null}
          {completedSteps.includes(Steps.Details) &&
          completedSteps.length === 1 &&
          companyTypes.includes(progressType) ? (
            <Flex mt={12}>
              <Banner color='warning' icon={InfoIcon}>
                <Text
                  variant='bodyBig'
                  fontWeight='fontWeightMedium'
                  color={theme.palette.text.primary}
                  whiteSpace='pre-line'
                >
                  <Flex flexDirection='column' gap={8}>
                    <Box>
                      <FormattedMessage
                        id='investment.progress.legalEntityBanner.idvAndQuestionnaire'
                        values={{
                          b: boldenText,
                        }}
                      />
                    </Box>
                    <Box>
                      <FormattedMessage
                        id='investment.progress.legalEntityBanner.note'
                        values={{
                          b: boldenText,
                        }}
                      />
                    </Box>
                    <Box>
                      <FormattedMessage
                        id='investment.progress.legalEntityBanner.contractSignature'
                        values={{
                          b: boldenText,
                        }}
                      />
                    </Box>
                  </Flex>
                </Text>
              </Banner>
            </Flex>
          ) : null}
        </Flex>
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={
          !completedSteps.includes(Steps.Details) && !user.personalDetailsLocked
            ? {
                title: intl.formatMessage({
                  id: 'misc.previous',
                }),
                onClick: onPreviousPress,
              }
            : undefined
        }
        primaryButton={{
          title: intl.formatMessage({
            id: isMobile ? stepData.button.mobileLabel : stepData.button.label,
          }),
          onClick: onSubmit,
        }}
      />
    </InvestmentContainer>
  );
};

export default Progress;
